import { Button, Card, DatePicker, Form, Input, Space, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type ApiError = { response?: { data?: { message?: string } } };
type Application = { id: string; record_version: number };

function asList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

export default function AdmissionOfferPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const fail = (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' }));
  const [issueForm] = Form.useForm();
  const [acceptForm] = Form.useForm();
  const [convertForm] = Form.useForm();

  const application = useQuery({
    queryKey: ['education-admission-version', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const rows = asList<Application>((await api.get('/education/admissions')).data);
      return rows.find((row) => row.id === id) || null;
    },
  });

  useEffect(() => {
    const version = application.data?.record_version;
    if (version == null) return;
    issueForm.setFieldValue('record_version', version);
    acceptForm.setFieldValue('record_version', version);
    convertForm.setFieldValue('record_version', version);
  }, [application.data?.record_version, issueForm, acceptForm, convertForm]);

  const issue = useMutation({
    mutationFn: (values: { terms: string; record_version: number; expires_at?: dayjs.Dayjs | null }) =>
      api.post(`/education/admissions/${id}/offer`, {
        ...values,
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD') : undefined,
        record_version: values.record_version ?? application.data?.record_version,
      }),
    onSuccess: () => message.success(t('saved')),
    onError: fail,
  });
  const accept = useMutation({
    mutationFn: (values: { record_version: number }) =>
      api.post(`/education/admissions/${id}/accept`, {
        record_version: values.record_version ?? application.data?.record_version,
      }),
    onSuccess: () => message.success(t('saved')),
    onError: fail,
  });
  const convert = useMutation({
    mutationFn: (values: { record_version: number; admission_no?: string }) =>
      api.post(`/education/admissions/${id}/convert`, {
        ...values,
        record_version: values.record_version ?? application.data?.record_version,
      }),
    onSuccess: () => message.success(t('saved')),
    onError: fail,
  });

  return (
    <EducationPageChrome
      title={t('offer')}
      description={t('admissionsIntro')}
      extra={
        <Button onClick={() => navigate(`/education/admissions?${params.toString()}`)}>
          {t('backToAdmissions')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('issueOffer')} loading={application.isLoading}>
          <Form form={issueForm} layout="vertical" onFinish={(values) => issue.mutate(values)} style={{ maxWidth: 520 }}>
            <Form.Item name="terms" label={t('offerTerms')} rules={[{ required: true }]}><Input.TextArea rows={4} /></Form.Item>
            <Form.Item
              name="expires_at"
              label={t('offerExpiresAt', { defaultValue: 'Offer valid until' })}
              extra={t('offerExpiresHint', { defaultValue: 'Optional. After this date the offer can no longer be accepted.' })}
            >
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" disabledDate={(d) => d.isBefore(dayjs(), 'day')} />
            </Form.Item>
            <Form.Item name="record_version" hidden><Input /></Form.Item>
            <Button type="primary" htmlType="submit" loading={issue.isPending}>{t('issueOffer')}</Button>
          </Form>
        </Card>
        <Card size="small" title={t('acceptOffer')}>
          <Form form={acceptForm} layout="vertical" onFinish={(values) => accept.mutate(values)} style={{ maxWidth: 480 }}>
            <Form.Item name="record_version" hidden><Input /></Form.Item>
            <Button htmlType="submit" loading={accept.isPending}>{t('acceptOffer')}</Button>
          </Form>
        </Card>
        <Card size="small" title={t('convertStudent')}>
          <Form form={convertForm} layout="vertical" onFinish={(values) => convert.mutate(values)} style={{ maxWidth: 480 }}>
            <Form.Item
              name="admission_no"
              label={t('admissionNumber')}
              extra={t('admissionNoConvertHint', { defaultValue: 'Optional; system may assign when blank' })}
            >
              <Input />
            </Form.Item>
            <Form.Item name="record_version" hidden><Input /></Form.Item>
            <Button htmlType="submit" loading={convert.isPending}>{t('convertStudent')}</Button>
          </Form>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
